/**
 * The primitives all three surfaces share.
 *
 * Deliberately small and deliberately not a framework. Each one exists because figure 4 or figure 5
 * draws it, and none of them holds state or knows about fetching: a component that decided when to
 * show an error would be a second place where the failure mapping lives.
 *
 * No colour, radius or font appears here. Everything reads from `tokens.ts` through the classes in
 * `surface.ts`, which is what makes a customer retheme one file under D-01.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { readThemePreference, setThemePreference, type ThemePreference } from './theme';

export interface PageProps {
  /** Small monospaced line above the title. The product name, usually. */
  readonly eyebrow?: string;
  readonly title: string;
  readonly lede?: ReactNode;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
}

/**
 * The single-column shell every guest screen sits in.
 *
 * The theme control lives in the footer of every one of them rather than on one screen, because a
 * prospect arrives on whichever screen their link points at and there is no navigation to put it in.
 * It is the last thing in the reading order on purpose: somebody who tapped a link in an email is here
 * to send a document, not to choose a colour.
 */
export function Page({ eyebrow, title, lede, children, footer }: PageProps): React.JSX.Element {
  return (
    <main className="ddds-page">
      {eyebrow !== undefined && <p className="ddds-eyebrow">{eyebrow}</p>}
      <h1 className="ddds-title">{title}</h1>
      {lede !== undefined && <p className="ddds-lede">{lede}</p>}
      {children}
      <footer className="ddds-footer">
        {footer}
        <ThemeToggle />
      </footer>
    </main>
  );
}

export interface CardProps {
  readonly title?: string;
  readonly children: ReactNode;
}

export function Card({ title, children }: CardProps): React.JSX.Element {
  return (
    <section className="ddds-card">
      {title !== undefined && <h2 className="ddds-card__title">{title}</h2>}
      {children}
    </section>
  );
}

export interface ButtonProps {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  readonly type?: 'button' | 'submit';
  readonly variant?: 'primary' | 'quiet';
  readonly size?: 'default' | 'small';
  /**
   * Full width is the phone default and stays the default.
   *
   * `auto` is for a button sitting beside another one - two header actions, say - where full width
   * would stack them into a column on a desktop. `small` already implies it.
   */
  readonly width?: 'full' | 'auto';
  readonly disabled?: boolean;
  /** Announced by a screen reader where the visible text is not enough on its own. */
  readonly label?: string;
}

export function Button({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'default',
  width = 'full',
  disabled = false,
  label,
}: ButtonProps): React.JSX.Element {
  const classes = ['ddds-button'];
  if (variant === 'quiet') classes.push('ddds-button--quiet');
  if (size === 'small') classes.push('ddds-button--small');
  if (width === 'auto' && size !== 'small') classes.push('ddds-button--auto');

  return (
    <button
      className={classes.join(' ')}
      type={type}
      onClick={onClick}
      disabled={disabled}
      {...(label === undefined ? {} : { 'aria-label': label })}
    >
      {children}
    </button>
  );
}

export interface FieldProps {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly error?: string;
  /** `code` gives the wide-tracked monospace treatment the six-digit entry needs. */
  readonly appearance?: 'default' | 'code';
  /** `email` gives a phone the at-sign keyboard, which D-25's sign-in needs. */
  readonly inputMode?: 'text' | 'numeric' | 'email';
  readonly autoComplete?: string;
  readonly maxLength?: number;
  readonly disabled?: boolean;
  /**
   * A handle on the input itself, for a control elsewhere that has to move focus here.
   *
   * The rep header's "Request a document" uses it: the field is already on the screen, so the button
   * takes you to it rather than opening a second copy in a dialog.
   */
  readonly inputRef?: React.Ref<HTMLInputElement>;
}

/**
 * A labelled input.
 *
 * The label is a real `<label>` bound by `htmlFor`, and the error and hint are wired through
 * `aria-describedby`. That is not decoration: this is the one screen a prospect has to get right,
 * and a mislabelled field on a phone with a screen reader is a person who cannot proceed at all.
 */
export function Field({
  id,
  label,
  hint,
  value,
  onChange,
  error,
  appearance = 'default',
  inputMode = 'text',
  autoComplete,
  maxLength,
  disabled = false,
  inputRef,
}: FieldProps): React.JSX.Element {
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const described = [hint === undefined ? null : hintId, error === undefined ? null : errorId]
    .filter((v): v is string => v !== null)
    .join(' ');

  const classes = ['ddds-field__input'];
  if (appearance === 'code') classes.push('ddds-field__input--code');

  return (
    <label className="ddds-field" htmlFor={id}>
      <span className="ddds-field__label">{label}</span>
      {hint !== undefined && (
        <span className="ddds-field__hint" id={hintId}>
          {hint}
        </span>
      )}
      <input
        className={classes.join(' ')}
        id={id}
        ref={inputRef}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        inputMode={inputMode}
        disabled={disabled}
        aria-invalid={error !== undefined}
        {...(described === '' ? {} : { 'aria-describedby': described })}
        {...(autoComplete === undefined ? {} : { autoComplete })}
        {...(maxLength === undefined ? {} : { maxLength })}
      />
      {error !== undefined && (
        <span className="ddds-field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </label>
  );
}

export interface StatusPillProps {
  readonly children: ReactNode;
  readonly tone?: 'done' | 'waiting' | 'quiet';
}

/**
 * A done/waiting state against "Upload", which is the whole of figure 5's checklist vocabulary.
 * The word itself belongs to the surface: a guest reads "Sent" and a rep reads "Received" for the
 * one `fulfilled` state, so this takes children rather than a state.
 */
export function StatusPill({ children, tone = 'quiet' }: StatusPillProps): React.JSX.Element {
  return <span className={`ddds-pill ddds-pill--${tone}`}>{children}</span>;
}

export interface NoticeProps {
  readonly children: ReactNode;
  readonly tone?: 'warn' | 'error';
  /**
   * The correlation id from the response.
   *
   * Shown rather than logged. Section 13 puts one in every response because it is the only thing a
   * customer can read back to us over the phone, and a prospect on a phone cannot open a console.
   */
  readonly reference?: string;
}

export function Notice({ children, tone = 'warn', reference }: NoticeProps): React.JSX.Element {
  return (
    <p className={`ddds-notice${tone === 'error' ? ' ddds-notice--error' : ''}`} role="alert">
      {children}
      {reference !== undefined && (
        <span className="ddds-notice__reference">Reference: {reference}</span>
      )}
    </p>
  );
}

export interface RowProps {
  readonly name: ReactNode;
  readonly meta?: ReactNode;
  readonly action?: ReactNode;
}

/** One line in a list: a checklist item, or a document. Figure 5 draws both this way. */
export function Row({ name, meta, action }: RowProps): React.JSX.Element {
  return (
    <li className="ddds-row">
      <div className="ddds-row__main">
        <div className="ddds-row__name">{name}</div>
        {meta !== undefined && <div className="ddds-row__meta">{meta}</div>}
      </div>
      {action !== undefined && <span className="ddds-row__action">{action}</span>}
    </li>
  );
}

export interface ProgressProps {
  /** 0 to 1. Clamped, because a byte count divided by a declared length can exceed it. */
  readonly value: number;
  readonly label: string;
}

/**
 * A real `<progress>`, not a div wearing `role="progressbar"`.
 *
 * The two are not equivalent. Assistive technology gets the value from the element itself rather than from
 * three ARIA attributes that have to agree with each other, and it keeps working where an ARIA value does
 * not - which for a prospect uploading a photograph from a phone is the case that matters. `surface.ts`
 * strips the platform appearance so the bar looks the same as before.
 *
 * The inner div is gone with it: the filled portion is now the browser's, styled through the two vendor
 * pseudo-elements, so there is no element left to put a width on.
 */
export function Progress({ value, label }: ProgressProps): React.JSX.Element {
  const percent = Math.round(Math.min(1, Math.max(0, value)) * 100);
  return <progress className="ddds-progress" value={percent} max={100} aria-label={label} />;
}

export interface AvatarProps {
  /** The person's name. Initials are derived; nothing else is shown. */
  readonly name: string;
  readonly size?: 'default' | 'large';
}

/**
 * Initials in a circle, standing in for a photograph nobody has uploaded.
 *
 * Decorative rather than informative: the name it was built from is always rendered beside it, so
 * announcing "DW" to a screen reader after it has just read "Dana Whitfield" is noise. Hence
 * `aria-hidden`.
 */
export function Avatar({ name, size = 'default' }: AvatarProps): React.JSX.Element {
  const initials = name
    .split(/\s+/)
    .filter((part) => part !== '')
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <span className={`ddds-avatar${size === 'large' ? ' ddds-avatar--lg' : ''}`} aria-hidden="true">
      {initials}
    </span>
  );
}

export interface ThemeToggleProps {
  /** Announced as the group's name. Defaults to something sensible. */
  readonly label?: string;
}

const THEME_OPTIONS: readonly { readonly value: ThemePreference; readonly label: string }[] = [
  { value: 'system', label: 'Auto' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

/**
 * Light, dark, or whatever the device says.
 *
 * Three options rather than a two-state switch, because "follow my device" is a real answer and the
 * default one - a switch can only express the two overrides, so choosing either would strand somebody
 * who simply wants their phone's sunset behaviour back.
 *
 * `aria-pressed` on three buttons rather than a radio group: these take effect immediately and are not
 * a form field being filled in, and a toggle button is what that reads as. The state lives here rather
 * than in a context because there is exactly one of these on a page and the attribute on the document
 * is the real state - `theme.ts` owns that, and this is only a control for it.
 *
 * Not rendered by the widget. An embedded panel follows its host; see `applyHostTheme`.
 */
export function ThemeToggle({ label = 'Colour theme' }: ThemeToggleProps): React.JSX.Element {
  const [preference, setPreference] = useState<ThemePreference>(() => readThemePreference());

  return (
    <div className="ddds-theme" role="group" aria-label={label}>
      {THEME_OPTIONS.map((option) => (
        <button
          key={option.value}
          className="ddds-theme__option"
          type="button"
          aria-pressed={preference === option.value}
          onClick={() => {
            setThemePreference(option.value);
            setPreference(option.value);
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
